/**
 * External process watchdog (Windows only — that's where stores run).
 *
 * A fully hung Chromium process cannot restart itself (timers, DevTools, even
 * window close are dead). So the app:
 *  1. writes a heartbeat file every keepalive tick
 *  2. spawns a detached VBScript that checks the heartbeat file age; if stale
 *     (> STALE_SEC) it kills this process tree and relaunches the exe.
 *
 * Watchdog exits by itself when this pid is gone (normal quit / restartApp),
 * and every new app instance spawns its own watchdog — no orphans pile up.
 * VBScript + WMI work on XP / 7 / 10 without extra tools (no taskkill needed).
 */
const HEARTBEAT_FILE = 'autoprint_heartbeat.txt';
const WATCHDOG_FILE = 'autoprint_watchdog.vbs';
const STALE_SEC = 180;
const CHECK_MS = 30_000;

const isWinNw = () => typeof global !== 'undefined' && process.platform === 'win32';

const getPath = (name: string) => {
    const path = require('path');
    return path.join(nw.App.dataPath, name);
};

/** Call on every keepalive tick. */
export const writeHeartbeat = () => {
    if (!isWinNw()) {
        return;
    }
    try {
        require('fs').writeFileSync(getPath(HEARTBEAT_FILE), String(Date.now()), 'utf8');
    } catch (error) {
        console.warn('watchdog: heartbeat write failed', error);
    }
};

const buildVbs = () => {
    return [
        "' Autoprint watchdog: relaunch app when heartbeat file is stale",
        'Set args = WScript.Arguments',
        'heartbeatPath = args(0)',
        'exePath = args(1)',
        'workDir = args(2)',
        'appPid = CLng(args(3))',
        'staleSec = CLng(args(4))',
        'checkMs = CLng(args(5))',
        'Set fso = CreateObject("Scripting.FileSystemObject")',
        'Set shell = CreateObject("WScript.Shell")',
        'Set wmi = GetObject("winmgmts:\\\\.\\root\\cimv2")',
        '',
        'Function IsRunning(pid)',
        '  Set procs = wmi.ExecQuery("Select ProcessId from Win32_Process where ProcessId = " & pid)',
        '  IsRunning = (procs.Count > 0)',
        'End Function',
        '',
        'Do',
        '  WScript.Sleep checkMs',
        '  If Not IsRunning(appPid) Then WScript.Quit',
        '  ageSec = 0',
        '  On Error Resume Next',
        '  ageSec = DateDiff("s", fso.GetFile(heartbeatPath).DateLastModified, Now())',
        '  If Err.Number <> 0 Then ageSec = 0',
        '  On Error GoTo 0',
        '  If ageSec > staleSec Then',
        '    Set procs = wmi.ExecQuery("Select * from Win32_Process where ProcessId = " & appPid & " or ParentProcessId = " & appPid)',
        '    For Each p In procs',
        '      On Error Resume Next',
        '      p.Terminate',
        '      On Error GoTo 0',
        '    Next',
        '    WScript.Sleep 3000',
        '    shell.CurrentDirectory = workDir',
        '    shell.Run """" & exePath & """", 1, False',
        '    WScript.Quit',
        '  End If',
        'Loop',
        '',
    ].join('\r\n');
};

/** Call once at boot (after nw is ready). */
export const startProcessWatchdog = () => {
    if (!isWinNw()) {
        return;
    }
    try {
        const fs = require('fs');
        const path = require('path');
        const vbsPath = getPath(WATCHDOG_FILE);
        const heartbeatPath = getPath(HEARTBEAT_FILE);
        fs.writeFileSync(vbsPath, buildVbs(), 'utf8');
        writeHeartbeat();
        const child = require('child_process').spawn(
            'wscript.exe',
            [
                '//B',
                '//Nologo',
                vbsPath,
                heartbeatPath,
                process.execPath,
                path.dirname(process.execPath),
                String(process.pid),
                String(STALE_SEC),
                String(CHECK_MS),
            ],
            { detached: true, stdio: 'ignore', windowsHide: true },
        );
        child.unref();
        console.log('watchdog: started', vbsPath);
    } catch (error) {
        console.warn('watchdog: start failed', error);
    }
};
